import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { carrierProfileSchema, type CarrierProfile } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient, getCsrfHeaders } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { X, CheckCircle, AlertCircle, Loader2, Search, ChevronDown } from "lucide-react";
import { SignupHeader } from "@/components/SignupHeader";
import { useCsrfToken } from "@/hooks/use-csrf-token";
import { EQUIPMENT_TYPES } from "@/lib/equipmentTypes";

interface FMCSACarrierInfo {
  dotNumber: string;
  legalName: string;
  dbaName?: string;
  mcNumber?: string;
  phone?: string;
  allowedToOperate: boolean;
  totalDrivers?: number;
  totalPowerUnits?: number;
}

interface VerificationResult {
  success: boolean;
  carrier?: FMCSACarrierInfo;
  error?: string;
  errors?: string[];
  verified: boolean;
  allowedToOperate: boolean;
}


const REGION_OPTIONS = [
  "Northeast",
  "Southeast",
  "Midwest",
  "Southwest",
  "West Coast",
  "Pacific Northwest",
  "Great Lakes",
  "Gulf Coast"
];

export default function SignupCarrier() {
  useCsrfToken();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [truckTypes, setTruckTypes] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual" | "free" | null>(null);
  const [verificationStatus, setVerificationStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [verificationResult, setVerificationResult] = useState<VerificationResult | null>(null);
  const [isIntrastate, setIsIntrastate] = useState(false);
  const setupSessionAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      setupSessionAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billing = params.get("billing") as "monthly" | "annual" | "free" | null;
    if (billing === "monthly" || billing === "annual" || billing === "free") {
      setBillingCycle(billing);
    } else {
      toast({
        title: "Invalid access",
        description: "Please select a membership plan first",
        variant: "destructive",
      });
      setLocation("/select-membership/carrier");
    }
  }, [toast, setLocation]);

  const verifyCarrier = async () => {
    const dotNumber = form.getValues("dotNumber");
    const mcNumber = form.getValues("mcNumber");

    if (!dotNumber && !mcNumber) {
      toast({
        title: "Missing Information",
        description: "Please enter a DOT or MC number to verify",
        variant: "destructive",
      });
      return;
    }

    setVerificationStatus("loading");
    setVerificationResult(null);

    try {
      const response = await fetch("/api/carriers/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
        body: JSON.stringify({ dotNumber, mcNumber: isIntrastate ? undefined : mcNumber, role: "Carrier" }),
      });

      const result: VerificationResult = await response.json();
      setVerificationResult(result);

      if (result.success && result.carrier) {
        setVerificationStatus("success");
        form.setValue("company", result.carrier.legalName);
        if (result.carrier.mcNumber) {
          form.setValue("mcNumber", result.carrier.mcNumber);
        }
        if (result.carrier.dotNumber) {
          form.setValue("dotNumber", result.carrier.dotNumber);
        }
        if (result.carrier.phone) {
          form.setValue("phone", result.carrier.phone);
        }
        if (result.carrier.totalPowerUnits) {
          form.setValue("fleetSize", result.carrier.totalPowerUnits);
        }
        toast({
          title: "Carrier Verified",
          description: `Found: ${result.carrier.legalName}`,
        });
      } else {
        setVerificationStatus("error");
        toast({
          title: "Verification Failed",
          description: result.error || "Could not verify carrier credentials",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      setVerificationStatus("error");
      toast({
        title: "Verification Error",
        description: "Failed to connect to verification service",
        variant: "destructive",
      });
    }
  };

  const form = useForm<CarrierProfile>({
    resolver: zodResolver(carrierProfileSchema),
    defaultValues: {
      userType: "Carrier",
      company: "",
      phone: "",
      dotNumber: "",
      mcNumber: "",
      isIntrastate: false,
      fleetSize: undefined,
      truckTypes: [],
      operatingRegions: [],
      hasInsurance: undefined as unknown as true,
      yearsInBusiness: undefined,
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: CarrierProfile) => {
      if (!billingCycle) {
        throw new Error("Billing cycle not selected");
      }
      const searchParams = new URLSearchParams(window.location.search);
      const billing = searchParams.get("billing") ?? "monthly";
      await apiRequest("POST", "/api/profile/complete", {
        ...data,
        isIntrastate,
        truckTypes,
        operatingRegions: regions,
        billingCycle,
        billing,
      });
    },
    onSuccess: async () => {
      if (window.fbq) window.fbq('track', 'CompleteRegistration');
      if (billingCycle === "free") {
        await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
        setLocation("/dashboard");
        return;
      }
      try {
        const controller = new AbortController();
        setupSessionAbortRef.current = controller;
        const res = await fetch("/api/create-setup-session", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
          body: JSON.stringify({ billingCycle: billingCycle || 'monthly', membershipTier: "SearchAndBid" }),
          signal: controller.signal,
        });
        const data = await res.json();
        if (data.url) {
          window.location.href = data.url;
          return;
        }
        throw new Error("No checkout URL returned");
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        console.error("Failed to create Stripe setup session:", err);
        toast({
          title: "Payment Setup Required",
          description: "We couldn't set up payment collection. Please try again. A credit card is required to complete signup.",
          variant: "destructive",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const toggleTruckType = (type: string) => {
    setTruckTypes(prev => {
      const updated = prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type];
      form.setValue('truckTypes', updated, { shouldValidate: true });
      return updated;
    });
  };

  const handleIntrastateChange = (checked: boolean) => {
    setIsIntrastate(checked);
    form.setValue("isIntrastate", checked);
    if (checked) {
      form.clearErrors("mcNumber");
    }
  };

  const toggleRegion = (region: string) => {
    setRegions(prev => {
      const updated = prev.includes(region) ? prev.filter(r => r !== region) : [...prev, region];
      form.setValue('operatingRegions', updated, { shouldValidate: true });
      return updated;
    });
  };

  if (!billingCycle) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <SignupHeader backUrl="/select-membership/carrier" />
      <div className="flex items-center justify-center p-4 pt-24 min-h-screen">
        <Card className="max-w-2xl w-full">
          <CardHeader>
            <CardTitle className="text-2xl">Complete Your Carrier Profile</CardTitle>
          <CardDescription>
            Provide your carrier information to access loads and maximize your revenue
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit((data) => mutation.mutate(data))} className="space-y-0">

              {/* Section 1 — Business Information */}
              <div className="border-t pt-5 mb-5 space-y-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Business Information</p>

                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="ABC Trucking LLC" {...field} data-testid="input-company" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Number *</FormLabel>
                        <FormControl>
                          <Input placeholder="(555) 123-4567" {...field} data-testid="input-phone" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="yearsInBusiness"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Years in Business</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            placeholder="e.g., 5"
                            {...field}
                            value={field.value ?? ""}
                            onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                            data-testid="input-years-in-business"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="fleetSize"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>How many trucks do you operate? *</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          placeholder="e.g., 1, 2, 5"
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                          data-testid="input-fleet-size"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Section 2 — Credentials & Verification */}
              <div className="border-t pt-5 mb-5 space-y-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Credentials &amp; Verification</p>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="dotNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>DOT Number *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="1234567"
                            {...field}
                            data-testid="input-dot"
                            onChange={(e) => {
                              field.onChange(e);
                              setVerificationStatus("idle");
                              setVerificationResult(null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="mcNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>MC Number</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="123456"
                            {...field}
                            data-testid="input-mc"
                            onChange={(e) => {
                              field.onChange(e);
                              setVerificationStatus("idle");
                              setVerificationResult(null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isIntrastate"
                    checked={isIntrastate}
                    onCheckedChange={(checked) => handleIntrastateChange(checked === true)}
                    data-testid="checkbox-intrastate"
                  />
                  <div className="space-y-1">
                    <label htmlFor="isIntrastate" className="text-sm font-medium cursor-pointer leading-none">
                      I operate within my state only (intrastate carrier)
                    </label>
                    {isIntrastate && (
                      <p className="text-sm text-muted-foreground" data-testid="text-intrastate-note">
                        Intrastate carriers are not required to have an MC number. You are responsible for compliance with your state carrier regulations.
                      </p>
                    )}
                  </div>
                </div>

                <p className="text-sm text-muted-foreground">
                  Click Verify with FMCSA to instantly confirm your credentials and speed up your account approval.
                </p>

                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={verifyCarrier}
                    disabled={verificationStatus === "loading"}
                    data-testid="button-verify-carrier"
                  >
                    {verificationStatus === "loading" ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Verify with FMCSA
                      </>
                    )}
                  </Button>

                  {verificationStatus === "success" && verificationResult?.carrier && (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        Verified: {verificationResult.carrier.legalName}
                        {verificationResult.allowedToOperate ? "" : " (Not allowed to operate)"}
                      </span>
                    </div>
                  )}

                  {verificationStatus === "error" && (
                    <div className="space-y-1">
                      {verificationResult?.errors && verificationResult.errors.length > 1 ? (
                        <div className="space-y-1">
                          {verificationResult.errors.map((err, i) => (
                            <div key={i} className="flex items-start gap-2 text-destructive">
                              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                              <span className="text-sm">{err}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-destructive">
                          <AlertCircle className="w-4 h-4" />
                          <span className="text-sm">{verificationResult?.error || "Verification failed"}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {verificationResult?.carrier && verificationResult.errors && verificationResult.errors.length > 0 && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      This carrier is not currently authorized to operate. Please ensure your operating authority is active before completing registration.
                    </p>
                  </div>
                )}

                <FormField
                  control={form.control}
                  name="hasInsurance"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center space-x-2 rounded-md bg-muted p-4">
                        <FormControl>
                          <Checkbox
                            id="hasInsurance"
                            checked={field.value === true}
                            onCheckedChange={(checked) => field.onChange(checked === true ? true : undefined)}
                            data-testid="checkbox-has-insurance"
                          />
                        </FormControl>
                        <label htmlFor="hasInsurance" className="text-sm font-medium cursor-pointer leading-snug">
                          I have active cargo/liability insurance with a valid insurance certificate which can be supplied upon request.
                        </label>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Section 3 — Fleet Information */}
              <div className="border-t pt-5 pb-5 mb-5 space-y-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Fleet Information</p>

                <div className="space-y-3">
                  <FormLabel>Truck Types * <span className="text-muted-foreground font-normal">select at least one</span></FormLabel>
                  <div className="grid grid-cols-2 gap-1.5">
                    {EQUIPMENT_TYPES.map((type) => (
                      <div key={type} className="flex items-center gap-2">
                        <Checkbox
                          id={type}
                          checked={truckTypes.includes(type)}
                          onCheckedChange={() => toggleTruckType(type)}
                          data-testid={`checkbox-truck-${type}`}
                        />
                        <label htmlFor={type} className="text-sm cursor-pointer">
                          {type}
                        </label>
                      </div>
                    ))}
                  </div>

                  {form.formState.errors.truckTypes && (
                    <p className="text-sm text-destructive">At least one truck type is required</p>
                  )}
                </div>
              </div>

              {/* Section 4 — Operating Regions */}
              <div className="border-t pt-5 pb-5 mb-5 space-y-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Operating Regions</p>

                <div className="space-y-3">
                  <FormLabel>Regions * <span className="text-muted-foreground font-normal">select at least one</span></FormLabel>
                  <div className="grid grid-cols-2 gap-1.5">
                    {REGION_OPTIONS.map((region) => (
                      <div key={region} className="flex items-center gap-2">
                        <Checkbox
                          id={region}
                          checked={regions.includes(region)}
                          onCheckedChange={() => toggleRegion(region)}
                          data-testid={`checkbox-region-${region}`}
                        />
                        <label htmlFor={region} className="text-sm cursor-pointer">
                          {region}
                        </label>
                      </div>
                    ))}
                  </div>
                  {form.formState.errors.operatingRegions && (
                    <p className="text-sm text-destructive">At least one region is required</p>
                  )}
                </div>
              </div>

              <div className="border-t pt-5">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={mutation.isPending || truckTypes.length === 0 || regions.length === 0}
                  data-testid="button-complete-profile"
                >
                  {mutation.isPending ? "Saving..." : "Complete Profile"}
                </Button>
              </div>

            </form>
          </Form>
        </CardContent>
        </Card>
      </div>
    </div>
  );
}
